import React, { useEffect, useRef, useState, useCallback } from 'react';
import useTheme from './hooks/useTheme';
import Header from './components/Header';
import SessionForm from './components/SessionForm';
import StatusBar from './components/StatusBar';
import Transcript from './components/Transcript';

const SSE_URL = 'https://sttserver-4htm.onrender.com/stt/stream';

function storageKey(id) { return `stt_cursor_${id}`; }
function loadCursor(id) { return sessionStorage.getItem(storageKey(id)); }
function saveCursor(id, val) { sessionStorage.setItem(storageKey(id), val); }
function clearCursor(id) { sessionStorage.removeItem(storageKey(id)); }

export default function App() {
  const { mode, theme, toggle } = useTheme();

  const [time, setTime]           = useState(0);
  const [input, setInput]         = useState('');
  const [sessionId, setSessionId] = useState('');
  const [lines, setLines]         = useState([]);
  const [status, setStatus]       = useState('disconnected');

  const esRef        = useRef(null);
  const sessionIdRef = useRef('');
  const seenIds      = useRef(new Set());

  useEffect(() => {
    const id = setInterval(() => setTime(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const stopSSE = useCallback(() => {
    esRef.current?.close();
    esRef.current = null;
  }, []);

  const startSSE = useCallback((id, cursor) => {
    stopSSE();
    setStatus('connecting…');
    const url = new URL(SSE_URL);
    url.searchParams.set('sessionId', id);
    if (cursor) url.searchParams.set('lastEventId', cursor);

    const es = new EventSource(url.toString());
    esRef.current = es;

    es.onopen = () => setStatus('connected');
    es.onerror = () => setStatus('reconnecting…');
    es.onmessage = (e) => {
      setStatus('connected');
      const eventId = e.lastEventId;``
      if (eventId) saveCursor(id, eventId);
      if (eventId && seenIds.current.has(eventId)) return;
      if (eventId) seenIds.current.add(eventId);
      setLines(prev => [...prev, { id: eventId || Date.now().toString(), text: e.data }]);
    };
  }, [stopSSE]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !sessionIdRef.current) return;
      startSSE(sessionIdRef.current, loadCursor(sessionIdRef.current));
    };
    const onPageShow = (e) => { if (e.persisted) onVisibility(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [startSSE]);

  useEffect(() => {
    const s = new URLSearchParams(window.location.search).get('session');
    if (s) {
      setInput(s);
      sessionIdRef.current = s;
      setSessionId(s);
      seenIds.current = new Set();
      setLines([]);
      startSSE(s, loadCursor(s));
    }
    return stopSSE;
  }, [startSSE, stopSSE]);

  function joinSession(id) {
    sessionIdRef.current = id;
    setSessionId(id);
    seenIds.current = new Set();
    setLines([]);
    startSSE(id, loadCursor(id));
  }

  function handleJoin(e) {
    e.preventDefault();
    const id = input.trim();
    if (id) joinSession(id);
  }

  function handleStop() {
    stopSSE();
    clearCursor(sessionIdRef.current);
    sessionIdRef.current = '';
    setSessionId('');
    setStatus('disconnected');
    seenIds.current = new Set();
  }

  return (
    <div style={{ height: '100dvh', background: theme.page, display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: "'Segoe UI', Arial, sans-serif", transition: 'background 0.2s', overflow: 'hidden' }}>
      <div style={{ width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', height: '100%', padding: '24px 16px 16px', boxSizing: 'border-box' }}>
        <Header time={time} theme={theme} mode={mode} onToggle={toggle} />
        <SessionForm input={input} onChange={setInput} onJoin={handleJoin} onStop={handleStop} sessionId={sessionId} theme={theme} />
        <StatusBar sessionId={sessionId} status={status} theme={theme} />
        <Transcript lines={lines} theme={theme} />
      </div>
    </div>
  );
}
